import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import GirviPageShell from '../common/GirviPageShell';
import { listFilterFieldSx, listTableHeadSx } from '../common/DetailDialog';
import baseURL from '../../baseURL';
import { computeInterest, days360, resolvePledgeDhara, yearlyCompoundRelease } from '../../utils/dharaInterest';
import { DEFAULT_DHARA } from '../../constants/dhara';
import { printRemainderLetters } from '../../utils/printUtility';

const rupee = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function articleKey(article) {
  let id = article?.articleId;
  if (id && typeof id === 'object') id = id._id != null ? id._id : id;
  return id != null ? String(id) : '';
}

function num(value) {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function isOpenPledge(pledge) {
  const status = String(pledge?.status || '').toUpperCase();
  if (status === 'RELEASED' || status === 'CLOSED') return false;
  if (pledge?.loanDetails?.released) return false;
  return true;
}

function customerName(pledge) {
  const c = pledge?.customer || {};
  return `${c.firstName || ''} ${c.lastName || ''}`.trim();
}

function phoneDigits(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits.length >= 10 ? digits.slice(-10) : '';
}

function slabOf(months) {
  if (months <= 0) return { label: 'Above', order: 0 };
  if (months <= 3) return { label: '1 to 3 months', order: 1 };
  if (months <= 6) return { label: '4 to 6 months', order: 4 };
  if (months >= 8 && months <= 10) return { label: '8 to 10 months', order: 8 };
  return null;
}

function owedAt(pledge, extraMonths) {
  const loan = pledge?.loanDetails || {};
  const principal = num(loan.principalAmount);
  const rate = num(loan.rateOfInterest);
  const elapsed = pledge?.date ? Math.max(0, days360(pledge.date, new Date())) : 0;
  const days = elapsed + extraMonths * 30;
  let interest = 0;
  if (principal > 0 && rate > 0 && days > 0) {
    const compound = days > 360 ? yearlyCompoundRelease(principal, rate, days) : null;
    interest = compound
      ? compound.interest
      : computeInterest(principal, rate, days, resolvePledgeDhara(pledge, DEFAULT_DHARA)).interest;
  }
  const total = Math.max(0, principal + interest - num(pledge?.totalPrincipalPaid) - num(pledge?.totalInterestPaid));
  return {
    principal: Math.max(0, principal - num(pledge?.totalPrincipalPaid)),
    interest,
    total,
  };
}

function metalOf(pledge, masters, rates) {
  const byId = new Map((masters || []).map((a) => [String(a._id), a]));
  const types = new Set();
  let missingRate = false;
  let pure = 0;
  let amount = 0;
  const titles = [];

  (pledge?.articles || []).forEach((article) => {
    const id = articleKey(article);
    if (!id || !byId.has(id)) return;
    types.add(id);
    const rate = num(rates[id]);
    if (!(rate > 0)) {
      missingRate = true;
      return;
    }
    const net = num(article.netWeight ?? article.netWt);
    const purity = num(article.purity);
    if (!(net > 0) || !(purity > 0)) return;
    const grams = (net * purity) / 100;
    pure += grams;
    amount += grams * rate;
    const title = byId.get(id).title || 'Article';
    if (!titles.includes(title)) titles.push(title);
  });

  if (!types.size || missingRate || !(amount > 0)) return null;
  return { pure, amount, titles };
}

function reachRow(pledge, metal) {
  const now = owedAt(pledge, 0);
  const target = metal.amount;
  if (now.total >= target) {
    return {
      months: 0,
      monthInterest: now.interest,
      total: now.total,
      principal: now.principal,
      interestNow: now.interest,
      ...metal,
    };
  }
  for (let months = 1; months <= 10; months += 1) {
    const at = owedAt(pledge, months);
    if (at.total < target) continue;
    if (!slabOf(months)) return null;
    return {
      months,
      monthInterest: Math.max(0, at.total - now.total),
      total: at.total,
      principal: now.principal,
      interestNow: now.interest,
      ...metal,
    };
  }
  return null;
}

function reachMessage(row) {
  const metal = row.titles.join(' and ');
  const ticket = row.ticket;
  if (row.months <= 0) {
    return `Ticket ${ticket}. Principal and interest have reached the ${metal} amount of Rs ${Math.round(row.amount)}. Total now Rs ${Math.round(row.total)}.`;
  }
  return `Ticket ${ticket}. ${metal} amount is Rs ${Math.round(row.amount)}. Principal and interest reach this in ${row.months} months. Interest Rs ${Math.round(row.monthInterest)}. Total Rs ${Math.round(row.total)}.`;
}

function addMonths(date, months) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

const RateReachPage = () => {
  const [pledges, setPledges] = useState([]);
  const [masters, setMasters] = useState([]);
  const [rates, setRates] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printingId, setPrintingId] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      fetch(`${baseURL}/pledges/pledges`, { headers }).then((r) => r.json()),
      fetch(`${baseURL}/articles`, { headers }).then((r) => r.json()),
    ])
      .then(([pledgeRes, articleRes]) => {
        const list = pledgeRes?.success ? pledgeRes.data || [] : [];
        setPledges(Array.isArray(list) ? list : []);
        const articles = Array.isArray(articleRes) ? articleRes : articleRes?.data;
        setMasters(Array.isArray(articles) ? articles : []);
      })
      .catch(() => setError('Could not load pledges.'))
      .finally(() => setLoading(false));
  }, []);

  const articleBoxes = useMemo(
    () => [...masters].sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''))),
    [masters]
  );

  const anyRate = articleBoxes.some((a) => num(rates[String(a._id)]) > 0);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map();
    pledges.filter(isOpenPledge).forEach((pledge) => {
      const metal = metalOf(pledge, masters, rates);
      if (!metal) return;
      const figured = reachRow(pledge, metal);
      if (!figured) return;
      const ticket = pledge?.loanDetails?.ticketNo || '';
      const name = customerName(pledge);
      if (q) {
        const ticketText = String(ticket).toLowerCase();
        const ticketHit = /^\d+$/.test(q) ? ticketText.startsWith(q) : ticketText.includes(q);
        if (!ticketHit && !name.toLowerCase().includes(q)) return;
      }
      const row = {
        id: pledge._id,
        ticket,
        name: name || '—',
        mobile: pledge?.customer?.mobileNo || '',
        pledge,
        ...figured,
      };
      const slab = slabOf(row.months);
      if (!slab) return;
      if (!map.has(slab.label)) map.set(slab.label, { ...slab, rows: [] });
      map.get(slab.label).rows.push(row);
    });
    return Array.from(map.values())
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) => a.months - b.months || String(a.ticket).localeCompare(String(b.ticket), undefined, { numeric: true })),
      }));
  }, [pledges, masters, rates, search]);

  const rowCount = groups.reduce((sum, group) => sum + group.rows.length, 0);

  const openShare = (row, kind) => {
    const digits = phoneDigits(row.mobile);
    if (!digits) {
      window.alert('No mobile number on this pledge.');
      return;
    }
    const text = encodeURIComponent(reachMessage(row));
    const url = kind === 'whatsapp'
      ? `https://wa.me/91${digits}?text=${text}`
      : `sms:+91${digits}?body=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const printLetter = async (row) => {
    const customer = row.pledge?.customer || {};
    const relation = String(customer.relation || '').trim().toUpperCase();
    const fatherName = relation === 'S/O' || relation === 'FATHER' ? customer.relationName : '';
    const due = row.months > 0 ? addMonths(new Date(), row.months) : new Date();
    setPrintingId(row.id);
    try {
      await printRemainderLetters([{
        customer: {
          customerCode: customer.code,
          customerName: row.name,
          firstName: customer.firstName,
          lastName: customer.lastName,
          fatherName,
          address: customer.address,
          occupation: customer.occupation,
          mobileNo: customer.mobileNo,
          village: customer.village,
          city: customer.village,
        },
        pawnTickets: [{
          ticketNo: row.ticket,
          date: row.pledge?.date,
          principalAmount: row.principal,
        }],
        noticeDate: new Date(),
        finalDueDate: due,
      }]);
    } catch (err) {
      window.alert(err?.message || 'Could not print the reminder letter.');
    } finally {
      setPrintingId('');
    }
  };

  return (
    <GirviPageShell
      title="Rate Reach"
      subtitle="Pledge · All pledges"
      chipLabel="Pledge"
      maxWidth="xl"
      loading={loading}
    >
      <Paper elevation={0} sx={{ mb: 2, p: { xs: 1.5, md: 2 }, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.5 }}>
          Enter the rate per gram of pure. Pure is net weight × purity ÷ 100. Amount is pure × rate.
          Over one year, interest is compound. Within a year, it is simple.
          If principal and interest are already above that amount, the pledge shows under Above.
        </Typography>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
          {articleBoxes.map((article) => (
            <TextField
              key={article._id}
              size="small"
              type="number"
              label={`${article.title || 'Article'} rate`}
              value={rates[String(article._id)] || ''}
              onChange={(e) => setRates((prev) => ({ ...prev, [String(article._id)]: e.target.value }))}
              sx={{ ...listFilterFieldSx, width: 180 }}
              inputProps={{ min: 0, step: '0.01' }}
            />
          ))}
          <TextField
            size="small"
            label="Search ticket or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...listFilterFieldSx, width: 240 }}
          />
        </Stack>
        {!articleBoxes.length && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>Add Gold or Silver in Article Master first.</Alert>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && anyRate && rowCount === 0 && (
        <Alert severity="info">No open pledge reaches these rates. Check purity on the articles, or enter the rate for every metal on the pledge.</Alert>
      )}

      {!loading && !anyRate && articleBoxes.length > 0 && (
        <Alert severity="info">Enter a rate to see the pledges.</Alert>
      )}

      {groups.map((group) => (
        <Paper key={group.label} elevation={0} sx={{ mb: 2, borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: '#0b1220', color: '#f8fafc', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 800 }}>{group.label}</Typography>
            <Typography sx={{ fontSize: 13, color: '#e2e8f0' }}>{group.rows.length}</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Ticket', 'Name', 'Metal', 'Pure', 'Amount', 'Months', 'Month × int', 'Total', ''].map((head) => (
                    <TableCell key={head || 'actions'} sx={{ ...listTableHeadSx, cursor: 'default' }}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {group.rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.ticket}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.titles.join(' + ')}</TableCell>
                    <TableCell>{row.pure.toFixed(3)} g</TableCell>
                    <TableCell>{rupee(row.amount)}</TableCell>
                    <TableCell>{row.months === 0 ? 'Above' : row.months}</TableCell>
                    <TableCell>{rupee(row.monthInterest)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{rupee(row.total)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        <Button size="small" variant="outlined" disabled={printingId === row.id} onClick={() => printLetter(row)}>
                          {printingId === row.id ? <CircularProgress size={16} /> : 'Reminder letter'}
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openShare(row, 'whatsapp')}>WhatsApp</Button>
                        <Button size="small" variant="outlined" onClick={() => openShare(row, 'sms')}>SMS</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </GirviPageShell>
  );
};

export default RateReachPage;
